import * as THREE from 'three';
import { bakeColored, type InstanceTransform } from './batch';
import { createGlowLayer, createLightPool, type GlowSpot } from './glow';
import { mesh, rod } from './helpers';
import type { ParkMaterials } from './materials';

/**
 * COL-011B attraction evolution — shared vocabulary for level 2 / level 3 dressing.
 *
 * Level 1 keeps today's cute, simple silhouette. Level 2 adds trim, pennants, more bulbs and a
 * night halo ring. Level 3 stacks a crown/spire, bunting swags, signage, twinkling bulbs and a
 * warm ground pool so the ride reads as the park's destination piece — especially at night.
 * Everything here bakes into `mats.baked` or joins an existing instanced bulb list, so the
 * added draw-call cost per attraction is one glow layer and one light pool.
 */
export type VisualTier = 1 | 2 | 3;

export function visualTier(level: number | undefined): VisualTier {
  if (!level || level <= 1) return 1;
  return level >= 3 ? 3 : 2;
}

export type XYZ = [number, number, number];

/** Evenly spaced points on a horizontal circle. */
export function ringPositions(count: number, radius: number, y: number, phase = 0): XYZ[] {
  return Array.from({ length: count }, (_, i) => {
    const angle = phase + (i / count) * Math.PI * 2;
    return [Math.cos(angle) * radius, y, Math.sin(angle) * radius] as XYZ;
  });
}

export function bulbTransforms(positions: XYZ[], scale = 1): InstanceTransform[] {
  return positions.map((position) => ({ position, scale }));
}

/** Small camera-facing halo for every bulb position. */
export function haloSpots(positions: XYZ[], size: number): GlowSpot[] {
  return positions.map((position) => ({ position, size }));
}

/** Five-point star extruded flat; lies in the XY plane facing +Z. */
export function starGeometry(outer: number, inner = outer * 0.45, depth = outer * 0.25): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  for (let i = 0; i < 10; i += 1) {
    const r = i % 2 === 0 ? outer : inner;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const x = Math.cos(a) * r;
    const y = Math.sin(a) * r;
    if (i === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  }
  shape.closePath();
  const geometry = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  geometry.translate(0, 0, -depth / 2);
  return geometry;
}

/** Triangular pennant, tip pointing +X, hanging from its top-left corner at the origin. */
export function pennantGeometry(length: number, drop: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(length, -drop * 0.5);
  shape.lineTo(0, -drop);
  shape.closePath();
  // thin extrude (not ShapeGeometry) so the flag is visible from both sides once baked
  return new THREE.ExtrudeGeometry(shape, { depth: 0.012, bevelEnabled: false });
}

const PENNANT_COLORS = ['red', 'cream', 'yellow', 'sky', 'pink', 'mint'] as const;

/** Small flags standing up along a ring (canopy rims, deck edges). */
export function pennantRing(mats: ParkMaterials, count: number, radius: number, y: number, size: number, phase = 0): THREE.Group {
  const group = new THREE.Group();
  ringPositions(count, radius, y, phase).forEach((position, i) => {
    const angle = phase + (i / count) * Math.PI * 2;
    const pole = mesh(new THREE.CylinderGeometry(0.012, 0.012, size * 1.6, 4), mats.iron, {
      position: [position[0], position[1] + size * 0.8, position[2]],
    });
    const flag = mesh(pennantGeometry(size * 0.9, size * 0.55), mats[PENNANT_COLORS[i % PENNANT_COLORS.length]], {
      position: [position[0], position[1] + size * 1.6, position[2]],
      rotation: [0, -angle + Math.PI / 2, 0],
      cast: false,
    });
    group.add(pole, flag);
  });
  return group;
}

/** Sagging rope of little flags between two points (bunting swag). */
export function buntingSwag(mats: ParkMaterials, from: THREE.Vector3, to: THREE.Vector3, flags = 5, sag = 0.14): THREE.Group {
  const group = new THREE.Group();
  const mid = from.clone().lerp(to, 0.5);
  mid.y -= sag;
  const curve = new THREE.QuadraticBezierCurve3(from, mid, to);
  const points = curve.getPoints(6);
  for (let i = 0; i < points.length - 1; i += 1) {
    group.add(rod(points[i]!, points[i + 1]!, 0.012, mats.rope, 4));
  }
  for (let i = 0; i < flags; i += 1) {
    const t = (i + 0.5) / flags;
    const point = curve.getPoint(t);
    const flag = mesh(new THREE.ConeGeometry(0.05, 0.11, 3), mats[PENNANT_COLORS[i % PENNANT_COLORS.length]], {
      position: [point.x, point.y - 0.06, point.z],
      rotation: [Math.PI, 0, 0],
      cast: false,
    });
    group.add(flag);
  }
  return group;
}

/** Ring of bunting swags between equally spaced anchor points. */
export function buntingRing(mats: ParkMaterials, count: number, radius: number, y: number, phase = 0, flags = 4): THREE.Group {
  const group = new THREE.Group();
  const anchors = ringPositions(count, radius, y, phase).map((p) => new THREE.Vector3(...p));
  anchors.forEach((anchor, i) => {
    group.add(buntingSwag(mats, anchor, anchors[(i + 1) % anchors.length]!, flags, 0.12));
  });
  return group;
}

/** Gold star topper standing upright on a short finial. */
export function starTopper(mats: ParkMaterials, size: number, y: number): THREE.Group {
  const group = new THREE.Group();
  group.add(mesh(new THREE.CylinderGeometry(0.02, 0.03, size * 0.9, 6), mats.gold, { position: [0, y - size * 0.45, 0] }));
  group.add(mesh(starGeometry(size), mats.gold, { position: [0, y + size * 0.6, 0], rotation: [0, Math.PI / 4, 0] }));
  return group;
}

/** Round entrance sign board with a bright frame — pure shape, no text, readable by any child. */
export function signMedallion(mats: ParkMaterials, radius: number, face: THREE.Material): THREE.Group {
  const group = new THREE.Group();
  group.add(mesh(new THREE.CylinderGeometry(radius, radius, 0.05, 20), mats.gold, { rotation: [Math.PI / 2, 0, 0] }));
  group.add(mesh(new THREE.CylinderGeometry(radius * 0.82, radius * 0.82, 0.06, 20), face, { rotation: [Math.PI / 2, 0, 0] }));
  group.add(mesh(starGeometry(radius * 0.5), mats.cream, { position: [0, 0, 0.04] }));
  return group;
}

/** Bakes a decorative prototype into one vertex-coloured mesh so dressing stays at one draw call. */
export function bakedDecor(proto: THREE.Object3D, mats: ParkMaterials, options: { cast?: boolean } = {}): THREE.Mesh {
  const baked = new THREE.Mesh(bakeColored(proto), mats.baked);
  baked.castShadow = options.cast ?? false;
  baked.receiveShadow = false;
  return baked;
}

/**
 * Night signature for a lit attraction: one halo layer for its bulbs and one warm ground pool.
 * Returns the two objects to add to the (non-rotating) root so they stay camera-faced.
 */
export function nightDressing(
  mats: ParkMaterials,
  halos: GlowSpot[],
  pool: { radius: number; y: number } | null,
  cool = false,
): THREE.Object3D[] {
  const items: THREE.Object3D[] = [];
  if (halos.length > 0) {
    items.push(createGlowLayer(cool ? mats.glowCool : mats.glowWarm, halos));
  }
  if (pool) {
    items.push(createLightPool(mats.lightPool, pool.radius, pool.y));
  }
  return items;
}

/**
 * Level 3 twinkle: scales bulb instances in a chasing pattern. Cheap (a few dozen matrix
 * writes per frame) and only applied to the extra bulb ring, never to the base geometry.
 */
export function createTwinkle(bulbs: THREE.InstancedMesh, positions: XYZ[], baseScale = 1): (elapsed: number) => void {
  const scratch = new THREE.Object3D();
  return (elapsed: number) => {
    const step = Math.floor(elapsed * 6);
    positions.forEach((position, index) => {
      const phase = (index + step) % 3;
      const scale = phase === 0 ? baseScale * 1.25 : phase === 1 ? baseScale : baseScale * 0.62;
      scratch.position.set(...position);
      scratch.scale.setScalar(scale);
      scratch.updateMatrix();
      bulbs.setMatrixAt(index, scratch.matrix);
    });
    bulbs.instanceMatrix.needsUpdate = true;
  };
}
